import { AccountSettingsType, CreateAccountRequest } from '../../types';
import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators';
import { isDefined, undefinedIfEmpty } from '../../helpers/utils';
import { redirectToLogin, setAccount } from '../../helpers/api/account-api';

import { Dialog } from '@material/mwc-dialog';
import { StateController } from '../../helpers/state/state-management/state-controller';
import accountInfo from '../../helpers/state/account-info';
import organizationInfo from '../../helpers/state/organization-info';
import subscriptionInfo from '../../helpers/state/subscription-info';
import { validateIcon } from '../../helpers/tags-categories';
import { getMainPage } from '../../helpers/page-name';

@customElement('account-info-page')
export class AccountInfoPage extends LitElement {
    static amplitude_callsite: string = 'Account Page';

    @query('#editNameSection')
    private readonly editNameSection!: HTMLDivElement;

    @query('#confirmationDialog')
    confirmationDialog!: Dialog;

    @state()
    imageUrl: string | undefined;

    @state()
    userName: string | undefined;

    @state()
    isEditingName: boolean = false;

    @state()
    settingSection?: string;

    @state()
    rotatePreviewCSS: string = '';

    @state()
    showImageUploader: boolean = false;

    private readonly bindAccountState = new StateController(this, accountInfo);

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        if (!accountInfo.isAuthenticated) {
            redirectToLogin();
        }
        this.imageUrl = accountInfo.image;
        this.userName = accountInfo.fullName;

        window.dispatchEvent(new CustomEvent('page-content-ready'));
    }

    async handleChangeName(e: any) {
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            const updatedAccount: CreateAccountRequest = {
                _id: accountInfo.id,
                full_name: e.detail.value,
                email: accountInfo.email,
                bookmarks: accountInfo.bookmarks,
                followed_profiles: accountInfo.followedProfiles,
                phone_number: accountInfo.phoneNumber,
                image_url: accountInfo.image,
                migrated_from_personal: accountInfo.migrated_from_personal ?? false,
            };

            await setAccount(AccountInfoPage.amplitude_callsite, updatedAccount, {
                update: 'Full Name',
            });

            accountInfo.fullName = e.detail.value;
        }, 'An error has occurred.  Context: could not update the account.');
    }

    toggleEdit() {
        this.isEditingName = !this.isEditingName;
        this.editNameSection.style.maxHeight = this.isEditingName ? '5rem' : '0';
    }

    private onUpdateSettingSection(e: any) {
        this.settingSection = e.detail.settingSectionInfo.settingSection;
    }

    private async logout() {
        await accountInfo.logout();
    }

    private async appSumoUpgrade() {
        window.open('https://appsumo.com/account/products/', '_blank');
    }

    async onChangeImageUrl (e: CustomEvent) {
        const validatedIcon = await validateIcon(e.detail?.imageUrl);
        if (!validatedIcon) {
            getMainPage().errorToastElement.showToastAndThrow('The icon URL entered is invalid');
        }
        this.rotatePreviewCSS = '';
        this.imageUrl = e.detail?.imageUrl;

        await this.handleChangeField({
            detail: {
                value: this.imageUrl
            }
        }, 'image_url');

        this.showImageUploader = false;
    }

    async handleChangeField(e: any, fieldName: string) {
        let newAccount: CreateAccountRequest = {
            _id: accountInfo.id,
            full_name: accountInfo.fullName,
            email: accountInfo.email,
            bookmarks: accountInfo.bookmarks,
            followed_profiles: accountInfo.followedProfiles,
            phone_number: accountInfo.phoneNumber,
            image_url: accountInfo.image,
            migrated_from_personal: accountInfo.account?.migrated_from_personal ?? false,
        };
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                newAccount = {
                    ...newAccount,
                    [fieldName]: e.detail.value,
                };
            await setAccount(AccountInfoPage.amplitude_callsite, newAccount, {
                update: fieldName,
            });
        }, 'An error has occurred.  Context: could not update the organization.');

        if (fieldName === 'full_name') {
            accountInfo.fullName = e.detail.value;
        } else if (fieldName === 'image_url') {
            accountInfo.image = e.detail.value;
        }
    }


    render() {
        return html`
            <div id="rootContainer">
                <div class="pageContainer">
                     <div class="headingContainer flexContainer">
                        <div class="headerTitleSection">
                            ${undefinedIfEmpty(accountInfo.image) ? html`
                                <account-avatar size="lg" imageUrl="${accountInfo.image}">
                                    ${organizationInfo.organization?.name?.charAt(0)}
                                </account-avatar>
                            ` : ''}
                            <h2 class="nameHeading">
                                ${undefinedIfEmpty(accountInfo.fullName) ? html`
                                    ${accountInfo.fullName}
                                    <br>
                                ` : ''}
                                ${accountInfo.email}
                            </h2>
                            <div class="helpHeader actionButton" @click="${this.logout}">
                                <mwc-icon class="buttonIcon">logout</mwc-icon>
                                <span>Sign out</span>
                            </div>
                        </div>
                    </div>
                    <div class="sidebar">
                        <settings-sidebar
                            type="Account"
                            .settingSection=${this.settingSection}
                            @on-select-filter-type=${this.onUpdateSettingSection}
                        >
                        </settings-sidebar>
                    </div>
                    <div class="mainContent">
                        ${this.settingSection === AccountSettingsType.General || !isDefined(this.settingSection) ? this.renderGeneral() : ''}
                        ${this.settingSection === AccountSettingsType.SubscriptionAndBilling ? this.renderSubscriptionBilling() : ''}
                        <div
                                class="helpHeader actionButton warning cardFooter flexEnd"
                            >
                                <mwc-icon class="buttonIcon" @click="${() => (this.confirmationDialog.open = true)}">delete</mwc-icon>
                                <span @click="${() => (this.confirmationDialog.open = true)}">Delete account</span>
                        </div>
                        <error-toast id="errorToast"></error-toast>
                    </div>
                </div>
            </div>
            <mwc-dialog id="confirmationDialog" class="dialog" hideActions>
                <modal-dialog size="md" @close="${() => this.confirmationDialog.close()}">
                    <confirm-delete-component slot="body"> </confirm-delete-component>
                </modal-dialog>
            </mwc-dialog>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    private async submitName() {
        await this.handleChangeField({
            detail: {
                value: this.userName
            }
        }, 'full_name');

        this.requestUpdate();
    }

    renderGeneral() {
        return html`
        <div class="innerBox">
                <!-- Name -->
                <div class="innerItem flexContainer">
                    <div class="editWrapper">
                        <span class="formLabel">Your Name</span>
                        <div id="editNameSection" class="editSection">

                            <form-input
                                id="name"
                                type="text"
                                .value="${accountInfo.fullName ?? ''}"
                                @change="${async (e: any) => { this.userName = e.detail.value; await this.submitName(); }}"
                            >
                            </form-input>
                        </div>
                    </div>
                </div>
                <div class="innerItem flexContainer alignCenter">
                    <span class="formLabel">Picture</span>
                    <div class="editWrapper">
                        ${isDefined(undefinedIfEmpty(this.imageUrl)) ? html`
                            <account-avatar imageUrl="${this.imageUrl}">
                                ${organizationInfo.organization?.name?.charAt(0)}
                            </account-avatar>
                        ` : ''}
                    ${!this.showImageUploader ? html`
                        <mwc-icon-button @click="${() => {this.showImageUploader = true;}}" icon="upload"></mwc-icon-button>
                    ` : ''}
                    ${this.showImageUploader ? html`
                        <image-upload-component
                            .imageUrl="${organizationInfo.organization?.logo ?? ''}"
                            .autoUpload=${true}
                            @retreived-autoupload-url="${this.onChangeImageUrl }"
                        ></image-upload-component>
                        <button @click="${() => {this.showImageUploader = false;}}" class="button">Cancel</button>
                    ` : ''}
                    </div>
                </div>
            </div>
        `;
    }

    renderSubscriptionBilling() {
        return html`
         ${accountInfo.isAppSumoUser === true && subscriptionInfo.hasActiveSubscription ? html`
            <div class="card cardBody">
                <h1>AppSumo License</h1>
                <p> Hi Sumo-ling - Thanks for buying a Proxi licence via AppSumo! Here's what is included:</p>
                <ul>
                    <li>No-code map builder</li>
                    <li>Crowdsource map points</li>
                    <li>Custom categories and tags</li>
                    <li>Spreadsheet management</li>
                    <li>Embed anywhere</li>
                    <li>Custom colors and icons</li>
                    <li>Map co-editors and owners</li>
                    <li>Capture viewer email addresses</li>
                    <li>Print maps</li>
                    <li>Advanced point customization: hide images, hide address, hide "Featured on", date/time, special offers</li>
                </ul>
                <p> <b> plus...</b></p>
                <div class="horizontal-flex">
                    <div class="innerBox">
                        <h2>AppSumo Tier 1</h2>
                        <p> One-time payment of $59</p>
                        <br>
                        <ul>
                            <li>All features above included</li>
                            <li>500 map points</li>
                            <li>25,000 lifetime renders included per account</li>
                            <li>$65 per additional 50K renders: charged via invoice from Proxi</li>
                        </ul>
                        <div class="action-button">
                            ${subscriptionInfo.subscription?.plan_id === 'AppSumo-Tier-1-USD-Yearly' ? html`
                                <p><b>Current Plan</b></p>
                            ` : '' }
                        </div>
                    </div>
                    <div class="innerBox">
                        <h2>AppSumo Tier 2</h2>
                        <p> One-time payment of $129</p>
                        <br>
                        <ul>
                            <li>All features above included </li>
                            <li>All Tier 1 Features </li>
                            <li>1,000 map points</li>
                            <li>Advanced map insights</li>
                            <li>Duplicate maps</li>
                            <li>Customize crowdsourcing form fields</li>
                            <li>Advanced map customization</li>
                            <li>50,000 lifetime renders included per account</li>
                            <li>$55 per additional 50K renders: charged via invoice from Proxi</li>
                            </ul>
                        <div class="action-button">
                            ${subscriptionInfo.subscription?.plan_id === 'AppSumo-Tier-2-USD-Yearly' ? html`
                                <p style="text-align:center"><b>Current Plan</b></p>
                            ` : '' }
                        </div>
                        </div>
                        <div class="innerBox">
                            <h2>AppSumo Tier 3</h2>
                            <p> One-time payment of $499</p>
                            <br>
                            <ul>
                                <li>All features above included </li>
                                <li>All Tier 1 and Tier 2 Features </li>
                                <li>10,000 map points</li>
                                <li>Password Protect Your Maps</li>
                                <li>Put a Paywall on Your Map</li>
                                <li>Turn your map into an interactive passport, challenge, or scavenger hunt.</li>
                                <li>100,000 lifetime renders included per account</li>
                                <li>$45 per additional 50K renders: charged via invoice from Proxi</li>
                            </ul>
                            <div class="action-button">
                                ${subscriptionInfo.subscription?.plan_id === 'AppSumo-Tier-3-USD-Yearly' ? html`
                                    <p style="text-align:center"><b>Current Plan</b></p>
                                ` : '' }
                        </div>
                    </div>
                </div>
            </div>
            ` : html`
                <subscription-cards>
                </subscription-cards>
            `}
        `;
    }


    static get styles() {
        return css`
            #rootContainer {
                height: 100%;
                display: flex;
                flex-direction: column;
                overflow: hidden;
            }

            #body {
                padding: 20px;
            }

            .buttonIcon {
                --mdc-icon-size: 1.25rem;
                margin-right: 0.5rem;
            }

            .mainContent {
                flex: 1 1 0%;
                width: 100%;
                box-sizing: border-box;
                position: relative;
                padding: 1rem 2rem 0rem 2rem;
            }

            .sidebar {
                max-width: 100%;
                box-sizing: border-box;
                font-size: 12px;
            }

            mwc-icon-button {
                --mdc-icon-size: 18px;
                color: var(--theme-input-color);
                border-radius: 50%;
                box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
            }

            .innerItem {
                border-top: 1px solid var(--theme-border-color);
                padding-top: 1rem;
                padding-bottom: 1rem;
                flex: 1;
                gap: 20px;
            }

            .colorElement {
                margin-top: 0.75rem;
            }

            .formLabel {
                color: var(--theme-input-color);
                font-size: 0.75rem;
                font-weight: bold;
                display: block;
            }

            .editWrapper {
                display: flex;
                flex-direction: row;
                align-items: flex-start;
                gap: 1rem;
                align-items: center;
            }

            .labelColumn {
                width: 100%;
                max-width: 21rem;
                flex:1;
            }


            .innerBox {
                position: relative;
            }

            .editSection {
                overflow: hidden;
                padding-top: 0.5rem;
                margin-bottom: 0.5rem;
                transition: max-height 0.5s ease;
                flex: 1;
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 1rem;
            }

            .flexContainer {
                display: flex;
                justify-content: flex-start;
            }

            .alignCenter {
                align-items: center;
            }

            .flexEnd {
                justify-content: flex-end;
            }

            .headingContainer {
                margin-bottom: 2rem;
                align-items: center;
                justify-content: center;
                text-align: center;
            }

            .helpHeader {
                color: var(--main-primary-color);
            }

            .actionButton {
                display: flex;
                align-items: center;
                transition: color 0.3s ease;
                font-size: 0.75rem;
                cursor: pointer;
                justify-content: center;
            }

            .warning {
                color: var(--main-invalid-color);
            }

            .buttonIcon {
                --mdc-icon-size: 1.25rem;
                margin-right: 0.5rem;
            }

            mwc-icon-button {
                --mdc-icon-size: 18px;
                color: var(--theme-input-color);
            }

            .innerItem {
                padding-bottom: 1rem;
            }

            .logoPreview {
                height: 50px;
            }

            .logoPreviewheader {
                height: 70px;
            }

            .pageContainer {
                display: flex;
                width: 100%;
                flex: 1;
                padding: 0 2rem;
                box-sizing: border-box;
                align-items: stretch;
                overflow: auto;
                flex-direction: column
            }


            .card {
                position: relative;
                display: flex;
                flex-direction: column;
                min-width: 0;
                word-wrap: break-word;
                background-color: var(--theme-white-color);
                background-clip: border-box;
                border: 1px solid var(--theme-border-color);
                border-radius: 0.75rem;
                box-shadow: 0 0.125rem 0.125rem -0.125rem rgba(31, 27, 45, 0.08),
                    0 0.25rem 0.75rem rgba(31, 27, 45, 0.08);
            }

            .card > hr {
                margin-right: 0;
                margin-left: 0;
            }

            .cardBody {
                flex: 1 1 auto;
                padding: 1rem 3rem 2rem;
            }

            .cardFooter {
                margin: 3rem 0 1rem;
                justify-content: flex-end;
            }


            .formValue {
                color: var(--theme-input-color);
                font-size: 1rem;
                word-break: break-all;
            }

            .editIcon.editing {
                color: var(--main-moon-color);
            }

            .editIcon.done {
                color: var(--main-success-color);
            }

            .horizontal-flex {
                display: flex;
                flex-direction: row;
                gap: 1rem;
            }

            .action-button{
                height: 3rem;
                width: 100%;
            }

            @media only screen and (max-width: 550px) {
                .flexContainer {
                    flex-direction: column;
                    text-align: center;
                }
                .flexEnd {
                    justify-content: center;
                }

                .pageContainer {
                    padding: 0;
                }
            }


        `;
    }

}
